import { formatHours } from '../lib/date'
import { FileIcon, PaperclipIcon, PlusIcon, SearchIcon } from './Icons'

function initials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

function avatarHue(name) {
  return [...name].reduce((total, character) => total + character.charCodeAt(0), 0) % 360
}

function cellLabel(resource, day, entry) {
  const base = `${resource.name}, ${day.date.toLocaleDateString()}`
  if (!entry) return `${base}: no entry`
  if (entry.status === 'worked') return `${base}: ${formatHours(entry.hours)} hours worked`
  if (entry.status === 'leave') return `${base}: leave`
  if (entry.status === 'sick') return `${base}: sick leave`
  if (entry.status === 'holiday') return `${base}: holiday`
  return base
}

function cellContent(entry) {
  if (!entry) return null
  if (entry.status === 'worked') {
    return (
      <span className="hours-value">
        {formatHours(entry.hours)}<small>h</small>
      </span>
    )
  }
  if (entry.status === 'leave') return 'L'
  if (entry.status === 'sick') return 'SL'
  if (entry.status === 'holiday') return 'H'
  return null
}

export default function TimesheetGrid({
  resources,
  days,
  entryMap,
  artifacts,
  onCellClick,
  onArtifactsClick,
  onAddResource,
  isFiltered,
  onClearSearch,
}) {
  if (!resources.length) {
    if (isFiltered) {
      return (
        <div className="empty-state">
          <span className="empty-state-icon"><SearchIcon size={24} /></span>
          <h3>No matching resources</h3>
          <p>Try another resource name.</p>
          <button className="button button--secondary" onClick={onClearSearch} type="button">
            Clear search
          </button>
        </div>
      )
    }

    return (
      <div className="empty-state">
        <span className="empty-state-icon"><PlusIcon size={24} /></span>
        <h3>Add your first resource</h3>
        <p>Create a row, then click a day to enter hours or mark leave.</p>
        <button className="button button--primary" onClick={onAddResource} type="button">
          <PlusIcon size={17} />
          Add resource
        </button>
      </div>
    )
  }

  return (
    <div className="table-scroll" role="region" aria-label="Monthly timesheet grid" tabIndex="0">
      <table className="timesheet-table">
        <thead>
          <tr>
            <th className="resource-head" scope="col">
              <span>Resource</span>
              <small>{resources.length} active</small>
            </th>
            {days.map((day) => (
              <th
                className={`day-head ${day.isWeekend ? 'is-weekend' : ''} ${day.isToday ? 'is-today' : ''}`}
                key={day.key}
                scope="col"
              >
                <span>{day.weekday}</span>
                <strong>{day.number}</strong>
              </th>
            ))}
            <th className="total-head" scope="col">Total</th>
            <th className="evidence-head" scope="col">Files</th>
          </tr>
        </thead>
        <tbody>
          {resources.map((resource) => {
            const resourceEntries = days
              .map((day) => entryMap.get(`${resource.id}:${day.key}`))
              .filter(Boolean)
            const totalHours = resourceEntries.reduce(
              (sum, entry) => sum + (entry.status === 'worked' ? Number(entry.hours || 0) : 0),
              0,
            )
            const resourceArtifacts = artifacts.filter(
              (artifact) => artifact.resource_id === resource.id,
            )

            return (
              <tr key={resource.id}>
                <th className="resource-cell" scope="row">
                  <span
                    className="resource-avatar"
                    style={{ '--avatar-hue': avatarHue(resource.name) }}
                  >
                    {initials(resource.name)}
                  </span>
                  <span className="resource-copy">
                    <strong>{resource.name}</strong>
                    <small>{resource.role || resource.email || 'Resource'}</small>
                  </span>
                </th>
                {days.map((day) => {
                  const entry = entryMap.get(`${resource.id}:${day.key}`)
                  const statusClass = entry ? `status-${entry.status}` : 'status-empty'
                  return (
                    <td
                      className={`day-data ${day.isWeekend ? 'is-weekend' : ''} ${
                        day.isToday ? 'is-today' : ''
                      }`}
                      key={day.key}
                    >
                      <button
                        aria-label={cellLabel(resource, day, entry)}
                        className={`day-button ${statusClass}`}
                        onClick={() => onCellClick(resource, day, entry || null)}
                        title={entry?.note || cellLabel(resource, day, entry)}
                        type="button"
                      >
                        {cellContent(entry)}
                      </button>
                    </td>
                  )
                })}
                <td className="total-cell">
                  <strong>{formatHours(totalHours)}</strong>
                  <small>hours</small>
                </td>
                <td className="evidence-cell">
                  <button
                    className={`evidence-button ${resourceArtifacts.length ? 'has-evidence' : ''}`}
                    onClick={() => onArtifactsClick(resource)}
                    type="button"
                  >
                    {resourceArtifacts.length ? <FileIcon size={16} /> : <PaperclipIcon size={16} />}
                    <span>{resourceArtifacts.length ? `${resourceArtifacts.length} file${resourceArtifacts.length === 1 ? '' : 's'}` : 'Upload'}</span>
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
